<script setup>
import { computed } from 'vue';

import { Bell } from 'lucide-vue-next';

import { useNotifications } from '@/composables/useNotifications';
import { CONNECTION_STATES } from '@/services/websocket';

const { unreadCount, connectionState, hasNewNotification, toggleDrawer } = useNotifications();

const connectionStatusClass = computed(() => {
  switch (connectionState.value) {
    case CONNECTION_STATES.CONNECTED:
      return 'status-connected';
    case CONNECTION_STATES.CONNECTING:
    case CONNECTION_STATES.RECONNECTING:
      return 'status-reconnecting';
    default:
      return 'status-disconnected';
  }
});

const connectionStatusTitle = computed(() => {
  switch (connectionState.value) {
    case CONNECTION_STATES.CONNECTED:
      return 'Connected';
    case CONNECTION_STATES.CONNECTING:
      return 'Connecting...';
    case CONNECTION_STATES.RECONNECTING:
      return 'Reconnecting...';
    default:
      return 'Disconnected';
  }
});

const displayCount = computed(() => {
  if (unreadCount.value > 99) {
    return '99+';
  }
  return unreadCount.value;
});
</script>

<template>
  <button
    class="notification-bell"
    :class="{ 'has-new': hasNewNotification }"
    :title="`Notifications (${connectionStatusTitle})`"
    @click="toggleDrawer"
  >
    <span class="bell-icon">
      <Bell :size="20" />
    </span>
    <span
      v-if="unreadCount > 0"
      class="notification-badge"
    >
      {{ displayCount }}
    </span>
    <span
      class="connection-status"
      :class="connectionStatusClass"
      :title="connectionStatusTitle"
    />
  </button>
</template>

<style lang="scss" scoped>
.notification-bell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--sub-bg);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--wp-surface-input);
  }

  &.has-new {
    animation: bell-ring 0.5s ease-in-out;
  }
}

.bell-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--wp-text-primary);
}

.notification-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--wp-state-main);
  color: var(--wp-constant-white);
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
}

.connection-status {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid var(--wp-surface-bg-icon);
  transition: background-color var(--transition-fast);

  &.status-connected {
    background-color: var(--wp-constant-success);
  }

  &.status-reconnecting {
    background-color: var(--wp-constant-warning);
    animation: pulse 1.5s ease-in-out infinite;
  }

  &.status-disconnected {
    background-color: var(--wp-red);
  }
}

@keyframes bell-ring {
  0% {
    transform: rotate(0);
  }
  15% {
    transform: rotate(15deg);
  }
  30% {
    transform: rotate(-15deg);
  }
  45% {
    transform: rotate(10deg);
  }
  60% {
    transform: rotate(-10deg);
  }
  75% {
    transform: rotate(5deg);
  }
  100% {
    transform: rotate(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
